// packages/shared/src/schemas/reminder.ts
// Reminder create/update validators. Replaces the worker's local
// `embedJson: z.string().min(1)` with a real shape-aware pipeline:
//   string -> JSON.parse -> embedSchema -> JSON.stringify
//
// The worker stores the canonical stringified form in D1 (column stays TEXT);
// SPA forms submit a JSON string and receive the same canonical string back.
//
// Phase 4 (Plan 04-02) extension:
// - event_id / event_match_mode / event_lead_minutes optional fields added.
// - superRefine validates: when triggerType=event AND event_match_mode=specific, event_id is required.
// - updateReminderSchema is built from the base object .partial() to avoid
//   the ZodEffects.partial() limitation (superRefine returns ZodEffects).

import { z } from 'zod';
import { embedSchema } from './embed';

// Shared superRefine rule applied to both create and update schemas.
function validateEventBinding(
  data: {
    triggerType?: string;
    event_match_mode?: string | null;
    event_id?: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (data.triggerType === 'event' && data.event_match_mode === 'specific' && !data.event_id) {
    ctx.addIssue({
      code: 'custom',
      path: ['event_id'],
      message: 'Event must be selected for specific binding',
    });
  }
}

// Base object shape (used for .partial() derivation).
const createReminderBaseSchema = z.object({
  name:      z.string().min(1).max(100),
  channelId: z
    .string()
    .regex(/^\d{17,20}$/, 'must be a Discord snowflake (17-20 digits)'),
  embedJson: z
    .string()
    .min(1)
    .transform((s, ctx) => {
      try {
        return JSON.parse(s) as unknown;
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'embedJson must be valid JSON',
        });
        return z.NEVER;
      }
    })
    .pipe(embedSchema)
    .transform((v) => JSON.stringify(v)),
  scheduleCron:       z.string().optional(),
  triggerType:        z.enum(['cron', 'event', 'both']).default('cron'),
  enabled:            z.boolean().default(true),
  // Phase 4 (Plan 04-02): event-trigger binding fields — optional for backward compatibility
  event_id:           z.string().nullable().optional(),
  event_match_mode:   z.enum(['specific', 'all']).nullable().optional(),
  event_lead_minutes: z.number().int().positive().nullable().optional(),
});

export const createReminderSchema = createReminderBaseSchema.superRefine(validateEventBinding);

// updateReminderSchema: partial of the base object + same superRefine applied independently.
export const updateReminderSchema = createReminderBaseSchema.partial().superRefine(validateEventBinding);

export type CreateReminderInput = z.infer<typeof createReminderSchema>;
export type UpdateReminderInput = z.infer<typeof updateReminderSchema>;
